<script lang="ts">
	import type { DateValue } from '@internationalized/date';
	import type { CalendarEvent } from '@/types/calendar';
	import CalendarEventComponent from '../CalendarEvent.svelte';
	import TimedEventBlock from '../TimedEventBlock.svelte';
	import * as ScrollArea from '@/components/ui/scroll-area';
	import {
		formatDayNumber,
		formatDayOfWeek,
		formatHour,
		isToday,
		getEventsForDate,
		getHoursArray,
	} from '../calendar.utils';

	interface Props {
		currentDate: DateValue;
		events: CalendarEvent[];
		onEventClick?: (event: CalendarEvent) => void;
		showPopover?: boolean;
		startHour?: number;
		endHour?: number;
	}

	let {
		currentDate,
		events,
		onEventClick,
		showPopover = true,
		startHour = 6,
		endHour = 20,
	}: Props = $props();

	const HOUR_HEIGHT_REM = 3;

	// Get events for the current day
	const dayEvents = $derived(getEventsForDate(events, currentDate));

	// Current day as YYYY-MM-DD for comparison
	const currentDayStr = $derived(
		`${currentDate.year}-${String(currentDate.month).padStart(2, '0')}-${String(currentDate.day).padStart(2, '0')}`,
	);

	/**
	 * Categorise events relative to the currently viewed day:
	 * - "timed": event starts or ends on this day with a meaningful time
	 * - "all-day": event spans the whole day (started before, ends after) or has no times
	 */
	const allDayEvents = $derived(dayEvents.filter((e) => !isTimedForDay(e)));
	const timedEvents = $derived(dayEvents.filter((e) => isTimedForDay(e)));

	// Get array of hours to display
	const hours = $derived(getHoursArray(startHour, endHour));

	// Check if current date is today
	const today = $derived(isToday(currentDate));

	// Format day name and number for header
	const dayName = $derived(formatDayOfWeek(currentDate));
	const dayNumber = $derived(formatDayNumber(currentDate));

	/** Extract the date portion (YYYY-MM-DD) from an ISO string */
	function getDatePart(dateStr: string): string {
		return dateStr.split('T')[0];
	}

	/** Check if an ISO string contains a meaningful time (not just midnight/date-only) */
	function hasTime(dateStr: string | null): boolean {
		if (!dateStr) return false;
		if (!dateStr.includes('T')) return false;
		const timePart = dateStr.split('T')[1]?.replace(/[+-]\d{2}:\d{2}$/, '').replace(/Z$/, '');
		return !!timePart && !timePart.startsWith('00:00:00');
	}

	/**
	 * Determine if an event should be rendered in the time grid for the current day.
	 * True when the event starts or ends on this specific day with a non-midnight time.
	 */
	function isTimedForDay(event: CalendarEvent): boolean {
		if (event.allDay) return false;
		if (!event.start && !event.end) return false;

		const startsToday = event.start && getDatePart(event.start) === currentDayStr;
		const endsToday = event.end && getDatePart(event.end) === currentDayStr;

		// If it starts today with a real time, or ends today with a real time → timed
		if (startsToday && hasTime(event.start)) return true;
		if (endsToday && hasTime(event.end)) return true;

		return false;
	}

	/** Get the fractional hour from an ISO datetime string (e.g. 9:30 -> 9.5) */
	function getHourFromDate(dateStr: string): number {
		const timePart = dateStr.split('T')[1];
		if (!timePart) return 0;
		const [h, m] = timePart.split(':').map(Number);
		return h + m / 60;
	}

	/**
	 * Get top/height in rem for a timed event within the grid.
	 * If the event started before today, it starts at the top of the grid.
	 * If it ends after today, it extends to the bottom.
	 */
	function getEventStyle(event: CalendarEvent): string {
		const startsToday = event.start && getDatePart(event.start) === currentDayStr;
		const endsToday = event.end && getDatePart(event.end) === currentDayStr;

		const eventStartHour = startsToday && event.start ? getHourFromDate(event.start) : startHour;
		const eventEndHour = endsToday && event.end ? getHourFromDate(event.end) : endHour + 1;

		const clampedStart = Math.max(eventStartHour, startHour);
		const clampedEnd = Math.min(eventEndHour, endHour + 1);

		const top = (clampedStart - startHour) * HOUR_HEIGHT_REM;
		const height = Math.max((clampedEnd - clampedStart) * HOUR_HEIGHT_REM, HOUR_HEIGHT_REM * 0.5);

		return `top: ${top}rem; height: ${height}rem;`;
	}
</script>

<div class="flex h-full flex-col rounded-lg border">
	<!-- Header -->
	<div
		class="sticky z-30 flex items-center gap-3 border-b bg-muted/40 p-4"
		style="top: var(--view-sticky-top, 0px)"
	>
		<div class="flex items-baseline gap-2">
			<span class="text-sm font-medium text-muted-foreground">{dayName}</span>
			<span class="text-2xl font-semibold">{dayNumber}</span>
		</div>
		{#if today}
			<span
				class="rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground"
			>
				Today
			</span>
		{/if}
		<span class="ml-auto text-sm text-muted-foreground">
			{dayEvents.length} {dayEvents.length === 1 ? 'event' : 'events'}
		</span>
	</div>

	<!-- Day View Content -->
	<ScrollArea.Root class="flex-1" orientation="vertical">
		{#if dayEvents.length === 0}
			<!-- Empty State -->
			<div class="flex h-full items-center justify-center p-8">
				<p class="text-muted-foreground">No events scheduled</p>
			</div>
		{:else}
			<!-- All-day events -->
			{#if allDayEvents.length > 0}
				<div class="space-y-1 border-b bg-muted/20 p-3">
					<span class="text-xs font-medium uppercase tracking-wide text-muted-foreground">All day</span>
					<div class="space-y-1">
						{#each allDayEvents as event (event.id)}
							<CalendarEventComponent
								{event}
								compact
								{showPopover}
								onclick={() => onEventClick?.(event)}
							/>
						{/each}
					</div>
				</div>
			{/if}

			<!-- Time Grid -->
			<div class="grid grid-cols-[auto_1fr]">
				<!-- Hour labels -->
				<div>
					{#each hours as hour (hour)}
						<div
							class="border-b pr-4 text-right text-sm text-muted-foreground"
							style="height: {HOUR_HEIGHT_REM}rem; line-height: {HOUR_HEIGHT_REM}rem;"
						>
							{formatHour(hour)}
						</div>
					{/each}
				</div>

				<!-- Hour rows + timed events overlay -->
				<div class="relative">
					<!-- Hour row lines -->
					{#each hours as hour (hour)}
						<div class="border-b" style="height: {HOUR_HEIGHT_REM}rem;"></div>
					{/each}

					<!-- Timed events positioned absolutely -->
					{#each timedEvents as event (event.id)}
						<div class="absolute left-1 z-10 w-48" style={getEventStyle(event)}>
							<TimedEventBlock
								{event}
								onclick={() => onEventClick?.(event)}
							/>
						</div>
					{/each}
				</div>
			</div>
		{/if}
	</ScrollArea.Root>
</div>